import { yupResolver } from '@hookform/resolvers/yup';
import { DatesRangeValue } from '@mantine/dates';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Switch } from '@zorro/design-system/v2/Switch';
import {
  formatDateISO,
  getTodayISO,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import {
  DateInput,
  DateRangeInput,
  FormErrorMessage,
  Grid,
  LabelWithTooltip,
  MonthPickerInput,
  Space,
  Stack,
} from '@zorro/web/design-system';
import {
  useBatchCallEndpoint,
  useForm,
  useIdempotencyKey,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

const updateEligibilityFormSchema = yup.object({
  shouldSetStartDate: yup.boolean(),
  startDate: yup.date().when('shouldSetStartDate', {
    is: true,
    then: () => yup.date().required(),
    otherwise: () => yup.date().notRequired(),
  }),
  shouldSetEndDate: yup.boolean(),
  endDate: yup.date().when('shouldSetEndDate', {
    is: true,
    then: () => yup.date().required(),
    otherwise: () => yup.date().notRequired(),
  }),
  shouldSetTerminationDate: yup.boolean(),
  shouldSetLeaveOfAbsence: yup.boolean(),
  leaveOfAbsence: yup.mixed<DatesRangeValue>().when('shouldSetLeaveOfAbsence', {
    is: true,
    then: () => yup.mixed<DatesRangeValue>().required(),
    otherwise: () => yup.mixed<DatesRangeValue>().notRequired(),
  }),
  terminationDate: yup.date().when('shouldSetTerminationDate', {
    is: true,
    then: () => yup.date().required(),
    otherwise: () => yup.date().notRequired(),
  }),
});

export type UpdateEligibilityFormFields = yup.InferType<
  typeof updateEligibilityFormSchema
>;

type UpdateEligibilityFormProps = {
  recordsForActions: RecordForActions[];
  onSuccess?: () => Promise<void> | void;
  onError?: () => void;
};

export function UpdateEligibilityForm({
  recordsForActions,
  onSuccess,
  onError,
}: UpdateEligibilityFormProps) {
  'use no memo';
  const idempotencyKey = useIdempotencyKey();
  const { executeBatchCall, errors: callEndpointErrors } = useBatchCallEndpoint(
    {
      methodName: 'employeesControllerUpdateEligibility',
      singularItemName: 'special onboarding period',
      action: 'updated',
      batchSize: 5,
    }
  );

  const startDate = recordsForActions.every(
    (employee) =>
      employee.eligibleFrom &&
      employee.eligibleFrom === recordsForActions[0].eligibleFrom
  )
    ? recordsForActions[0].eligibleFrom
    : undefined;

  const endDate = recordsForActions.every(
    (employee) =>
      employee.eligibleUntil &&
      employee.eligibleUntil === recordsForActions[0].eligibleUntil
  )
    ? recordsForActions[0].eligibleUntil
    : undefined;

  const leaveOfAbsence = recordsForActions.every(
    (record) =>
      record.leaveOfAbsenceStartDate &&
      record.leaveOfAbsenceEndDate &&
      record.leaveOfAbsenceStartDate ===
        recordsForActions[0].leaveOfAbsenceStartDate &&
      record.leaveOfAbsenceEndDate ===
        recordsForActions[0].leaveOfAbsenceEndDate
  )
    ? {
        startDate: recordsForActions[0].leaveOfAbsenceStartDate,
        endDate: recordsForActions[0].leaveOfAbsenceEndDate,
      }
    : undefined;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, isSubmitting, errors: formErrors },
  } = useForm<UpdateEligibilityFormFields>({
    mode: 'all',
    resolver: yupResolver(updateEligibilityFormSchema),
    defaultValues: {
      shouldSetStartDate: Boolean(startDate),
      startDate: startDate ? parseDateISO(startDate).toDate() : undefined,
      shouldSetEndDate: Boolean(endDate),
      endDate: endDate ? parseDateISO(endDate).toDate() : undefined,
      shouldSetLeaveOfAbsence: Boolean(leaveOfAbsence),
      leaveOfAbsence:
        leaveOfAbsence && leaveOfAbsence.startDate && leaveOfAbsence.endDate
          ? [
              parseDateISO(leaveOfAbsence.startDate).toDate(),
              parseDateISO(leaveOfAbsence.endDate).toDate(),
            ]
          : undefined,
    },
  });

  const updateEligibility = async (formFields: UpdateEligibilityFormFields) => {
    const eligibilityData = {
      eligibleFrom:
        formFields.startDate && formFields.shouldSetStartDate
          ? formatDateISO(formFields.startDate)
          : null,
      eligibleUntil:
        formFields.endDate && formFields.shouldSetEndDate
          ? formatDateISO(formFields.endDate)
          : null,
      leaveOfAbsence:
        formFields.leaveOfAbsence && formFields.shouldSetLeaveOfAbsence
          ? {
              startDate: formatDateISO(formFields.leaveOfAbsence[0] || ''),
              endDate: formatDateISO(formFields.leaveOfAbsence[1] || ''),
            }
          : null,
      terminationDate:
        formFields.terminationDate && formFields.shouldSetTerminationDate
          ? formatDateISO(formFields.terminationDate)
          : null,
      terminationReportDate:
        formFields.terminationDate && formFields.shouldSetTerminationDate
          ? getTodayISO()
          : null,
    };

    const batchItems = recordsForActions.map(({ fullName, employeeId }) => ({
      key: fullName,
      params: [
        employeeId,
        idempotencyKey.getFor(employeeId),
        eligibilityData,
      ] as const,
    }));

    await executeBatchCall(batchItems, {
      onSuccess: async () => await onSuccess?.(),
      onError,
    });
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          disabled: !isValid || isSubmitting,
          onClick: handleSubmit(updateEligibility),
        }}
      />
    ),
    [handleSubmit, updateEligibility, isValid, isSubmitting]
  );
  useDrawerFooter(drawerFooter);

  return (
    <form onSubmit={handleSubmit(updateEligibility)}>
      <Stack mih="calc(100vh - 140px)" px="xxl">
        <Grid>
          <Grid.Col span={{ sm: 6 }}>
            <Stack>
              <Controller
                control={control}
                name="shouldSetStartDate"
                render={({ field: { value, ref: _ref, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    label="Set eligibility start date"
                  />
                )}
              />
              <Controller
                control={control}
                name="startDate"
                render={({ field: { ref: _ref, ...rest } }) => (
                  <DateInput
                    {...rest}
                    label="Start date"
                    disabled={!watch('shouldSetStartDate')}
                  />
                )}
              />

              <FormErrorMessage errors={formErrors} fieldName="startDate" />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ sm: 6 }}>
            <Stack>
              <Controller
                control={control}
                name="shouldSetEndDate"
                render={({
                  field: { value, onChange, ref: _ref, ...rest },
                }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    onChange={(event) => {
                      onChange(event);
                      if (!event.currentTarget.checked) {
                        setValue('shouldSetTerminationDate', false);
                      }
                    }}
                    label="Set eligibility end date"
                  />
                )}
              />
              <Controller
                control={control}
                name="endDate"
                render={({ field: { onChange, ...rest } }) => (
                  <MonthPickerInput
                    {...rest}
                    type="default"
                    onChange={(date) => {
                      if (date instanceof Date) {
                        const lastDayOfMonth = parseDate(date)
                          .endOf('month')
                          .toDate();
                        onChange(lastDayOfMonth);
                      }
                    }}
                    placeholder="MM/DD/YYYY"
                    label={
                      <LabelWithTooltip
                        label="Last date of benefit eligibility"
                        isRequired
                      />
                    }
                  />
                )}
              />

              <FormErrorMessage errors={formErrors} fieldName="endDate" />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ sm: 6 }}>
            <Stack>
              <Controller
                control={control}
                name="shouldSetLeaveOfAbsence"
                render={({ field: { value, ref: _ref, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={value}
                    label="Set a leave of absence"
                  />
                )}
              />
              <Controller
                control={control}
                name="leaveOfAbsence"
                render={({ field: { ref: _ref, ...rest } }) => (
                  <DateRangeInput
                    {...rest}
                    label="Leave dates"
                    disabled={!watch('shouldSetLeaveOfAbsence')}
                  />
                )}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ sm: 6 }}>
            <Stack>
              <Controller
                control={control}
                name="shouldSetTerminationDate"
                render={({ field: { value, ref: _ref, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={!!value}
                    disabled={!watch('shouldSetEndDate')}
                    label="Set a last date of employment"
                  />
                )}
              />
              <Controller
                control={control}
                name="terminationDate"
                render={({ field }) => (
                  <DateInput
                    {...field}
                    required
                    label="Last date of employment"
                    disabled={
                      !watch('shouldSetTerminationDate') ||
                      !watch('shouldSetEndDate')
                    }
                  />
                )}
              />
              <FormErrorMessage
                errors={formErrors}
                fieldName="terminationDate"
              />
            </Stack>
          </Grid.Col>
        </Grid>

        <Space h="xl" />

        <ErrorsTable
          tableTitle="Employee name"
          errors={callEndpointErrors}
          isBulk={recordsForActions.length > 1}
        />
      </Stack>
    </form>
  );
}
